import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// POST /api/id-scan — read an identity document and get the name printed on it.
//
// Replaced the idware barcode proxy (/api/process-image), which could only read
// a PDF417 barcode or an MRZ on the photographed side — US driving licences and
// passports, and nothing else a front desk is handed.
//
// Uses the DEFAULT axios instance so the call inherits axios-setup.ts:
// withCredentials, the X-CSRF-Token double-submit header, and the single-flight
// 401 -> refresh -> retry.

// How the surname was decided. The FE shows a softer badge for the guesses,
// because a heuristic split off an unlabelled document genuinely deserves a
// second look and an MRZ-confirmed one does not.
export type ScanBasis =
  | 'mrz_confirmed'          // the machine-readable zone agreed with the printed name
  | 'visual_zone'            // labelled Surname/Given-names fields, no MRZ to check against
  | 'mrz_only'               // MRZ alone — right split, but diacritics are gone
  | 'heuristic'              // one undivided name line; the split is a guess
  | 'heuristic_family_first'; // ...guessed with the issuing country's name order

export interface IdScanSuccess {
  ok: true;
  first_name: string;
  // Given name(s) between the first and the family name. Empty for most people.
  middle: string;
  last_name: string;
  // 0-1. Already reduced by how much guessing the split required.
  confidence: number;
  basis: ScanBasis;
  // The printed name and the MRZ named different people — one was misread.
  // Not fatal (the printed value is the better spelling) but worth a second look.
  mrz_conflict: boolean;
  document_kind: string;
  // What the SCANNER proposed, before anyone touched it. Sent straight back as
  // provenance `raw`, which is what lets a later edit be recognised as a
  // correction rather than an original entry.
  raw: { first_name: string; middle_name: string; last_name: string };
}

export interface IdScanRefusal {
  ok: false;
  status: string;
  // Always ends with "type the name instead" — the scanner is an accelerator,
  // and the desk must never be BLOCKED by it being unavailable.
  message: string;
  // Machine-readable, so the UI can style this as a quota notice rather than a
  // scan failure without parsing English. Deliberately carries NO call to
  // action: there is no "buy more AI" product — the only purchasable thing is
  // units — so a Billing link would send an admin to buy something that does
  // not fix what failed.
  code?: 'IQ_QUOTA_EXCEEDED';
  // ISO date the monthly quota resets.
  resets_at?: string;
  document_kind?: string | null;
}

export type IdScanResult = IdScanSuccess | IdScanRefusal;

export interface IdScanInput {
  // A data: URL (what canvas.toDataURL() produces) or bare base64.
  image: string;
  buildingId?: number | null;
}

export const scanIdDocument = async ({
  image,
  buildingId,
}: IdScanInput): Promise<IdScanResult> => {
  const res = await axios.post<IdScanResult>(`${apiRoute}/id-scan`, {
    image,
    building_id: buildingId ?? undefined,
  });

  return res.data;
};

// ---------------------------------------------------------------------------
// POST /api/parcel-scan — read a shipping label.
// ---------------------------------------------------------------------------
//
// A different document class from an ID, with a different schema. What makes it
// worth its own call: a label carries THE UNIT, which narrows the resident
// picker to that unit's occupants and usually resolves the recipient with no
// further interaction.

export interface ParcelScanSuccess {
  ok: true;
  recipient_name: string;
  // Non-null when the label named an apartment this building actually has.
  // Null means the desk picks the unit by hand — which is why the unit search
  // has to be as good as the scan.
  unit_id: number | null;
  // Always populated: the inventory spelling when matched, the label's own text
  // when not, so the desk can see what the courier wrote.
  unit_label: string;
  unit_matched: boolean;
  // Two units share a normalised number. Neither is chosen; the desk decides.
  unit_ambiguous: boolean;
  carrier: string;
  tracking: string;
  confidence: number;
  raw: { recipient_name: string };
}

export type ParcelScanResult = ParcelScanSuccess | IdScanRefusal;

export const scanParcelLabel = async ({
  image,
  buildingId,
}: {
  image: string;
  buildingId: number;
}): Promise<ParcelScanResult> => {
  const res = await axios.post<ParcelScanResult>(`${apiRoute}/parcel-scan`, {
    image,
    building_id: buildingId,
  });

  return res.data;
};
